import useEmblaCarousel from "embla-carousel-react"
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import { useState } from "react"
import styled from "styled-components"
import { mediaSizes, defaultThemeColors, responsivePaddingX } from "ui/theme"
import {
  NextButton,
  PrevButton,
  useEmblaKeyboard,
  usePrevNextButtons,
} from "../Button/EmblaButtons"
import { InstructorCard } from "./InstructorCard"

export const InstructorCarousel = ({
  slides,
  options,
  title,
  onSlideClick,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    WheelGesturesPlugin(),
  ])
  const [isActive, setIsActive] = useState(false)

  const handleActivate = () => {
    setIsActive(true)
  }

  const handleDeactivate = () => {
    setIsActive(false)
  }

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi)

  useEmblaKeyboard(emblaApi, isActive)

  return (
    <Embla
      tabIndex={0}
      onMouseEnter={handleActivate}
      onMouseLeave={handleDeactivate}
      onFocus={handleActivate}
      onBlur={handleDeactivate}>
      <EmblaControls>
        <EmblaTitle>{title}</EmblaTitle>
        <EmblaButtons>
          <PrevButton
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          />
          <NextButton
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          />
        </EmblaButtons>
      </EmblaControls>

      <EmblaViewport ref={emblaRef}>
        <EmblaContainer>
          {slides?.map((slide, index) => (
            <InstructorCard
              key={index}
              instructor={slide}
              onClick={() => onSlideClick?.(slide.user_id)}
            />
          ))}
        </EmblaContainer>
      </EmblaViewport>
    </Embla>
  )
}

const Embla = styled.section`
  max-width: 100%;
  margin: auto;
`

const EmblaViewport = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0 ${responsivePaddingX.desktop};
  box-sizing: border-box;

  @media (max-width: ${mediaSizes.lg}) {
    padding: 0 ${responsivePaddingX.lg};
  }

  @media (max-width: ${mediaSizes.md}) {
    padding: 0 ${responsivePaddingX.md};
  }

  @media (max-width: ${mediaSizes.xs}) {
    padding: 0 ${responsivePaddingX.xs};
  }
`

const EmblaContainer = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: calc(20px * -1);
  margin-right: calc(80px - 20px);
  will-change: transform;
  backface-visibility: hidden;
`

const EmblaControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px ${responsivePaddingX.desktop} 2px ${responsivePaddingX.desktop};
  box-sizing: border-box;
  gap: 1.2rem;
  margin-bottom: 1.8rem;

  @media (max-width: ${mediaSizes.lg}) {
    padding: 8px ${responsivePaddingX.lg} 2px ${responsivePaddingX.lg};
  }

  @media (max-width: ${mediaSizes.md}) {
    padding: 8px ${responsivePaddingX.md} 2px ${responsivePaddingX.md};
  }

  @media (max-width: ${mediaSizes.xs}) {
    padding: 8px ${responsivePaddingX.xs} 2px ${responsivePaddingX.xs};
  }
`

const EmblaButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.6rem;
  align-items: center;
`

const EmblaTitle = styled.div`
  font-size: 32px;
  font-weight: 500;
  color: ${defaultThemeColors.basicBlack};
  margin: 0;
  line-height: 1.2;

  @media (max-width: ${mediaSizes.lg}) {
    font-size: 28px;
  }

  @media (max-width: ${mediaSizes.md}) {
    font-size: 26px;
  }

  @media (max-width: ${mediaSizes.xs}) {
    font-size: 22px;
  }
`
